/** Swiss number formatting, one set of formatters for the whole site. */

export const deCh = (opts?: Intl.NumberFormatOptions) => new Intl.NumberFormat("de-CH", opts);

/** "1'234", thousands separated, no decimals. */
export const num = deCh();
/** "4.75", always two decimals. */
export const dec2 = deCh({ minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** "3.1", always one decimal. */
export const dec1 = deCh({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
/** At most `max` decimals, e.g. `decimals(1).format(3.14)` → "3.1". */
export const decimals = (max: number) => deCh({ maximumFractionDigits: max });
/**
 * "1.7M" / "846k" / "820", the shortest magnitude label for chart axes and tight
 * gutters; de-CH decimal point, callers prepend any currency. Preferred over the
 * Intl compact notation for axes: de-CH's compact is not actually compact under a
 * million ("846'450") and abbreviates millions as the clunky "Mio.".
 */
export const shortNum = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${dec1.format(v / 1_000_000)}M`;
  if (a >= 1_000) return `${num.format(Math.round(v / 1_000))}k`;
  return num.format(Math.round(v));
};

/** "€ 1'234", rounded, with the space the copy uses everywhere. */
export const eur = (v: number) => `€ ${num.format(Math.round(v))}`;

/* Spelled out rather than delegated to `toLocaleDateString("it-IT")`: the
   abbreviations Intl produces depend on the host's ICU build, so Node and the
   browser can disagree ("5 ago 2026" vs "5 ago. 2026") and the prerendered
   markup would not match the hydrated one. */
const MONTHS_IT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

/** "05 ago 2026", from an ISO date string. */
export function itDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${day} ${MONTHS_IT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * `M x,y L x,y …` over `n` samples, the SVG path every hand-drawn curve on the
 * site was building with its own loop (the backdrops, the bento
 * market line). The sampling stays with the caller; only the string does not.
 */
export const polyline = (n: number, at: (i: number) => { x: number; y: number }) =>
  Array.from({ length: n }, (_, i) => {
    const { x, y } = at(i);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

/** "2025-08-13" or "2025-08-13T16:50:52+02:00" -> "13.08.2025". */
export function iso2EUDate(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split("-");
  return `${day}.${month}.${year}`;
}

/**
 * Locale-aware date formatting, one wrapper for the runtime-locale sites (the
 * booking calendar) so no feature file hand-rolls `Intl.DateTimeFormat`.
 */
export const localeDate = (locale: string, value: Date | string, opts: Intl.DateTimeFormatOptions): string =>
  new Intl.DateTimeFormat(locale, opts).format(typeof value === "string" ? new Date(value) : value);

/** "09:42", the current wall-clock time in Zurich (CET), for the booking timezone note. */
export const cetTime = (): string =>
  new Date().toLocaleTimeString("en-GB", { timeZone: "Europe/Zurich", hour: "2-digit", minute: "2-digit" });

type UnitScale = {
  value: number;
  baseUnit?: string;
  weight?: boolean;
  returnValueOnly?: boolean;
  returnUnitOnly?: boolean;
};

export function formatUnitScale({
  value,
  baseUnit = "kW",
  weight = false,
  returnValueOnly = false,
  returnUnitOnly = false,
}: UnitScale): string {
  const prefixes = [
    { symbol: weight ? "g" : "", factor: 1 },
    { symbol: weight ? "kg" : "k", factor: 1e3 },
    { symbol: weight ? "t" : "M", factor: 1e6 },
    { symbol: weight ? "Gg" : "G", factor: 1e9 },
    { symbol: weight ? "Tg" : "T", factor: 1e12 },
  ];
  const basePrefix =
    prefixes.find((p) => p.symbol && baseUnit.startsWith(p.symbol))?.symbol ?? "";
  const suffix = baseUnit.slice(basePrefix.length);
  const baseFactor = prefixes.find((p) => p.symbol === basePrefix)!.factor;
  const inSuffix = value * baseFactor;
  const best =
    [...prefixes].reverse().find((p) => inSuffix >= p.factor) ?? prefixes[0];
  const display = inSuffix / best.factor;
  const formatted = decimals(display < 10 ? 2 : display < 100 ? 1 : 0).format(display);
  const unit = `${best.symbol}${suffix}`;
  if (returnUnitOnly) return unit;
  if (returnValueOnly) return formatted;
  return `${formatted} ${unit}`;
}
